import {
  $InternalListState,
  $InternalList,
  fetchInternalList,
  removeInternalList,
} from "@store/internal-study/list-store";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { Checkbox, Tooltip } from "@mui/material";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { NotePencilIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useEffect } from "react";
import { ListShortDataT } from "@interfaces/internal-study/list";

import { Link } from "react-router-dom";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import clsx from "clsx";
import style from "@scss/pages/company/departments.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { useAsyncConfirm } from "@hooks/useAsyncConfirm";
import { useChoiceState } from "@hooks/useChoiceState";
import { useCompanyId } from "@store/company/company-store";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";

const InternalStudyListTable: React.FC = () => {
  const { isLoading, error, isFetched } = useUnit($InternalListState);
  const internalList = useUnit($InternalList);
  const permissions = useUnit($UserAddPermissions);
  const companyId = useCompanyId();

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    selectById,
    clearSelectedItems,
    getIsItemSelected,
  } = useChoiceState(internalList, (list) => list.id);

  const { open } = useModal();
  const confirm = useAsyncConfirm();

  const handleRemoveList = (list: ListShortDataT) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить обучение "${list.title}"?`,
      },
      onConfirm: () => {
        removeInternalList({ id: list.id });
      },
    });
  };

  const handleDeleteSelectedList = async () => {
    const ok = await confirm({
      modalData: {
        text: "Вы уверены, что хотите удалить выбранные обучения?",
      },
    });
    if (!ok) return;

    for (const list of selectedItems) {
      removeInternalList({ id: list.id });
    }

    clearSelectedItems();
  };

  useEffect(() => {
    if (!isFetched && companyId !== null) {
      fetchInternalList(companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableBodyContent = internalList.length ? (
    internalList.map((list) => {
      const isSelected = getIsItemSelected(list);
      const modalData = {
        modalTitle: "Изменить обучение",
        comissionId: list.id,
        title: list.title,
        editMode: true,
        comission: list.comission,
        trainees_members: list.trainees_members,
        process_members: list.process_members,
        finish_members: list.finish_members,
      };
      return (
        <tr key={list.id}>
          <td align="left">
            <span>
              <Checkbox
                color="primary"
                checked={isSelected}
                onChange={(e) => selectById(list, e.target.checked)}
              />
            </span>
            <Link
              to={`/study-center/internal/list/${list.id}`}
              className={clsx(style.wrapperRow, style.wrapperRowStudy)}
            >
              <label
                htmlFor={`key_${list.id}`}
                className={clsx(
                  tableStyle.column_fixed_height,
                  style.wrapperLabel
                )}
              >
                <label
                  id={`key_${list.id}`}
                  className={clsx(
                    tableStyle.checkbox_label,
                    style.wrapperLabel
                  )}
                >
                  <span className={style.wrapperLabel}>{list.title}</span>
                </label>
              </label>
            </Link>
          </td>
          <td align="left">{list.comission_info.title}</td>
          <td align="left">{list.trainees_members.length}</td>
          <td align="left">{list.process_members?.length}</td>
          <td align="left">{list.finish_members?.length}</td>
          <td>
            <div className={clsx(style.department_actions_col)}>
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_list_allowedToEdit
              ) && (
                <WrapperSVG color={"orange"}>
                  <Tooltip title="Изменить" placement="top">
                    <NotePencilIcon
                      size={24}
                      className={style.department_actions_col__icon}
                      onClick={() =>
                        open("CreateComissionModal", {
                          btnText: "Сохранить",
                          modalData: modalData,
                        })
                      }
                    />
                  </Tooltip>
                </WrapperSVG>
              )}
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_list_allowedToDelete
              ) && (
                <Tooltip title="Удалить" placement="top">
                  <TrashIcon
                    size={24}
                    className={style.department_actions_col__icon}
                    onClick={() => handleRemoveList(list)}
                  />
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td>
        <p style={{ textAlign: "center", width: "100%", margin: "24px 0px" }}>
          Обучений нет
        </p>
      </td>
    </tr>
  );

  return (
    <div
      className={clsx(
        tableStyle.base_table_container,
        style.departments_table,
        style.no_border_top
      )}
    >
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorIndicator />
      ) : (
        <table className={clsx(tableStyle.base_table)}>
          <thead>
            <tr>
              <td>
                <span>
                  <Checkbox
                    color="primary"
                    checked={areAllItemsSelected}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                </span>
                <label className={clsx(tableStyle.checkbox_label)}>
                  Название обучения
                </label>
              </td>
              <td align="left">Комиссия</td>
              <td align="left">Кол-во не обученных</td>
              <td align="left">Кол-во обучаемых</td>
              <td align="left">Кол-во обученных</td>
              <td align="right">
                {selectedItems.length > 0 && (
                  <span>
                    {permissions.hasPermission(
                      ACTIONS.educationalCenter_internalTraining_list_allowedToDelete
                    ) && (
                      <Tooltip title="Удалить" placement="top">
                        <TrashIcon
                          size={24}
                          className={style.department_actions_col__icon}
                          onClick={handleDeleteSelectedList}
                        />
                      </Tooltip>
                    )}
                  </span>
                )}
              </td>
            </tr>
          </thead>
          <tbody>{tableBodyContent}</tbody>
        </table>
      )}
    </div>
  );
};

export default InternalStudyListTable;
